import { CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { Menu, MenuModule } from 'primeng/menu';
import { TagModule } from 'primeng/tag';
import { filter, takeUntil } from 'rxjs';

import { UnsubscribeDirective } from '@component/shared/unsubscribe.directive';
import { MenuHelper } from '@helper/menu.helper';
import { AppService } from '@service/app.service';
import { AuthService } from '@service/auth.service';
import { KeyService } from '@service/key.service';
import { NotificationService } from '@service/notification.service';
import { VersionService } from '@service/version.service';
import { MenuItem } from '@type/menu.type';
import { ModuleValues } from '@type/module.type';
import { User } from '@type/user.type';
import { EnvironmentDomains, EnvironmentNames } from '@type/environment';

@Component({
  selector: 'app-navbar',
  imports: [
    RouterModule,
    CommonModule,
    MenuModule,
    ButtonModule,
    TagModule,
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
})
export class NavbarComponent extends UnsubscribeDirective {
  @ViewChild('menu') menu: Menu;

  tabs: MenuItem[] = [];

  readonly defaultVersion = 'unknown';

  uiVersion = this.defaultVersion;
  apiVersion = this.defaultVersion;

  environmentName: EnvironmentNames;

  get isDev(): boolean {
    if (!this.environmentName) {
      return false;
    }

    return this.environmentName === EnvironmentNames.LOCAL ||
      this.environmentName === EnvironmentNames.DEV ||
      this.environmentName === EnvironmentNames.STAGE;
  }

  constructor(
    private authService: AuthService,
    private router: Router,
    private appService: AppService,
    private versionService: VersionService,
    private keyService: KeyService,
    private readonly notificationService: NotificationService,
  ) {
    super();
  }

  get currentUser(): User {
    return this.authService.currentUser;
  }

  get menuItems(): MenuItem[] {
    return this.authService.menuItems;
  }

  get email(): string {
    return this.currentUser?.email;
  }

  get initials(): string {
    return this.email?.charAt(0).toLocaleUpperCase();
  }

  get selectedYardName(): string {
    return this.keyService.selectedYard?.name;
  }

  get currentModule(): ModuleValues {
    return this.appService.currentModule;
  }

  get unreadNotifications(): string {
    return this.notificationService.getNotificationStatsValues()?.unread.toString() || '0';
  }

  ngOnInit(): void {
    this.listenRouteChange();
    this.getUIVersion();
    this.getAPIVersion();
    this.setEnvironmentName();
  }

  openNotifications(): void {
    this.notificationService.toggleSidebar(true);
  }

  listenRouteChange(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy),
      )
      .subscribe(() => {
        this.appService.currentModule = this.router.url.split('/')[1] as ModuleValues;

        this.updateTabs();
      });
  }

  updateTabs(): void {
    this.tabs = MenuHelper.getNavbarMenuStructure(this.keyService)
      .filter(tab => tab.availableModules.includes(this.appService.currentModule) &&
        tab.availableRoles.includes(this.currentUser.role),
      );
  }

  logout(): void {
    this.authService.logout();
    this.menu.hide();
  }

  menuItemClick(): void {
    this.menu.hide();
  }

  getUIVersion(): void {
    this.versionService.getUIVersion()
      .subscribe(uiVersion => {
        this.uiVersion = this.sliceVersion(uiVersion);
      });
  }

  getAPIVersion(): void {
    if (!this.currentUser) {
      return;
    }

    this.versionService.getAPIVersion()
      .subscribe(apiVersion => {
        this.apiVersion = this.sliceVersion(apiVersion);
      });
  }

  sliceVersion(version: string): string {
    return version?.slice(0, 7) || this.defaultVersion;
  }

  confirmPasswordReset(): void {
    this.appService.confirm(() => {
      this.authService.resetPassword({
        login: this.authService.currentUser.email,
      })
        .subscribe();
    });
  }

  setEnvironmentName(): void {
    const domain = window.location.hostname as keyof typeof EnvironmentDomains;
    this.environmentName = EnvironmentDomains[domain] || null;
  }
}
